import { useState } from 'react';
import { AiOutlineUser } from 'react-icons/ai';
import MenuDropdown from './atoms/MenuDropdown';
import CustomModal from './atoms/CustomModal';
import { useDeleteReferralMutation, useGetOrderReferralDataQuery } from '../store';
import Button from './atoms/Button';
import { ReferralDTO } from '../types/Referral';
import CreateEditReferral from './CreateEditReferral';
import config from '../config/config';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import FilterTable, { BaseData } from './Table/Table';
import EmptyTable from './Table/EmptyTable';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import { calculateReferralPromo, useReferralPromo, useReferralTotals } from '../utils/referralUtils';
import OrderReferralProgressBar from './OrderReferralProgressBar';
import { formatCurrency } from '../utils/shared';
import Link from './icons/Link';
import { toastSuccess } from '../utils/toasts';

interface Props {
  referrals: ReferralDTO[];
  eventId: string;
  businessId: string;
  button?: {
    label: string;
    reactIcon: React.ReactNode;
    onClick: () => void;
    variant: 'primary' | 'secondary' | 'tertiary';
  };
  orderReferrals?: boolean;
}

interface TableReferrals extends BaseData {
  id: string;
  code: string;
  name: string;
  tickets_sold: number;
  active: JSX.Element;
  amount_sold: JSX.Element;
}

const ReferralsTable: React.FC<Props> = ({
  referrals, eventId, businessId, button, orderReferrals = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'delete' | 'edit' | 'progress'>('delete');
  const [deleteReferral] = useDeleteReferralMutation();
  const { business } = useGetSelectedBusiness();
  const { event } = useGetSelectedEvent();
  const [referral, setReferral] = useState<ReferralDTO | undefined>(undefined);
  const handleConfirmDeleteReferral = () => {
    deleteReferral({
      businessId,
      eventId,
      referralId: referral?.id || '',
    });
    toastSuccess('Referido eliminado correctamente');
    setShowModal(false);
  };
  const { data } = useGetOrderReferralDataQuery({
    orderId: referral?.owner_referral_order_id || '',
  }, { skip: !referral?.owner_referral_order_id || !orderReferrals });

  const referredOrders = data?.orders_by_referral || [];

  const totals = useReferralTotals(data || null, referredOrders);
  const promo = useReferralPromo(totals.totalTickets);

  const handleClose = () => setShowModal(false);

  const handleDeleteReferral = (re: ReferralDTO) => {
    setReferral(re);
    setModalType('delete');
    setShowModal(true);
  };

  const handleEditReferral = (re: ReferralDTO) => {
    setReferral(re);
    setModalType('edit');
    setShowModal(true);
  };

  const handleViewProgress = (re: ReferralDTO) => {
    setReferral(re);
    setModalType('progress');
    setShowModal(true);
  };

  const handleCopyReferralLink = (re: ReferralDTO) => {
    setReferral(re);
    const domain = business?.nickname
      ? `${business.nickname}.${config.domain}`
      : config.baseDomain;
    const link = business?.nickname
      ? `${domain}/events/${event?.slug}?ref=${re.code}`
      : `${domain}/events/${event?.slug}?ref=${re.code}`;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        toastSuccess('Link copiado al portapapeles');
      })
      .catch((err) => {
        console.error('Error al copiar al portapapeles: ', err);
      });
  };

  const dropDownOptions = !orderReferrals ? [
    {
      label: 'Copiar link',
      onClick: handleCopyReferralLink,
    },
    {
      label: 'Editar',
      onClick: handleEditReferral,
    },
    {
      label: 'Eliminar',
      onClick: handleDeleteReferral,
    },
  ] : [
    {
      label: 'Ver progreso',
      onClick: handleViewProgress,
    },
    {
      label: 'Copiar link personalizado',
      onClick: handleCopyReferralLink,
    },
  ];

  const tableConfig = !orderReferrals ? [
    {
      label: 'Nombre',
      id: 'name',
    },
    {
      label: 'Estado',
      id: 'active',
    },
    {
      label: 'Código',
      id: 'code',
    },
    {
      label: 'Veces usado',
      id: 'tickets_sold',
    },
  ] : [
    {
      label: 'Nombre',
      id: 'name',
    },
    // {
    //   label: 'Entrada a reintegrar',
    //   id: '',
    // },
    {
      label: 'Entradas vendidas',
      id: 'tickets_sold',
    },
    {
      label: 'Monto vendido',
      id: 'amount_sold',
    },
    {
      label: 'Reintegro %',
      id: 'reimbursement_percentage',
    },
    // {
    //   label: 'Monto devuelto',
    //   id: '',
    // },
  ];

  const actions = (row: TableReferrals) => {
    const ref = referrals.find((re) => re.id === row.id);
    if (!ref) {
      throw new Error('Referral not found');
    }
    return (
      <MenuDropdown<ReferralDTO>
        options={dropDownOptions}
        data={ref}
      />
    );
  };

  const rows: TableReferrals[] = referrals.map((r) => ({
    id: r.id,
    code: r.code,
    name: r.name,
    tickets_sold: r.tickets_sold,
    amount_sold: <span className='whitespace-nowrap'>{formatCurrency(r.amount_sold)}</span>,
    reimbursement_percentage: `${calculateReferralPromo(r.tickets_sold).currentReward}%`,
    active: r.active ? (
      <div className='tag-primary'>Habilitado</div>
    ) : (
      <div className='tag-warning'>No habilitado</div>
    ),
  }));

  const customButton = button && <Button onClick={button.onClick} variant={button.variant}>
    <div className='flex flex-row gap-2 items-center'>
      <Link className='text-xl' />
      <p className='whitespace-nowrap hidden md:block'>{button.label}</p>
    </div>
  </Button>;

  return (
    <div>
      {referrals.length > 0 ? (
        <FilterTable
          rows={rows}
          headCells={tableConfig}
          pagination={{
            enabled: true,
            pageSize: 10,
          }}
          searchEnabled={true}
          actions={actions}
          customButtons={customButton ? [customButton] : []}
          tableId="referrals-table"
        />
      ) : (
        <EmptyTable
          title="Aún no hay referidos en este evento"
          info="Aquí aparecerán todos los referidos que tengan progreso en este evento"
          icon={AiOutlineUser}
          iconColor="text-secondary-500"
          button={button ? [
            {
              label: button?.label,
              reactIcon: button?.reactIcon,
              onClick: button?.onClick,
              variant: button?.variant,
            },
          ] : undefined}
        />
      )}
      <CustomModal
        onRequestClose={handleClose}
        isOpen={showModal}
        hideCancelButton={true}
      >
        <div>
          {modalType === 'delete' && (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <h1 className="text-xl font-medium">Eliminar referido</h1>
                {referral?.tickets_sold && referral.tickets_sold > 0 ? (
                  <div>
                    <p>
                      Este referido no se puede eliminar porque ya tiene tickets vendidos.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p>¿Deseas eliminar este referido?</p>
                    <p className="mt-2">
                      Una vez eliminado desaparecerá de la lista y no se podrá revertir.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex flex-row gap-2">
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  {referral?.tickets_sold && referral.tickets_sold > 0 ? 'Aceptar' : 'Cancelar'}
                </Button>
                {!referral?.tickets_sold && <Button
                  variant="primary"
                  onClick={() => handleConfirmDeleteReferral()}
                >
                  Eliminar
                </Button>}
              </div>
            </div>
          )}
          {modalType === 'edit' && (
            <div>
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h1 className="text-xl font-medium">Editar referido</h1>
                  <p className="text-text-tertiary text-sm">
                    Podrás realizar las ediciones que necesites en este referido
                  </p>
                </div>
                <CreateEditReferral
                  referral={referral || undefined}
                  onSuccess={() => {
                    handleClose();
                    toastSuccess('Referido editado correctamente');
                  }}
                  onCancel={handleClose}
                />
              </div>
            </div>
          )}
          {modalType === 'progress' && (
            <div className='flex flex-col gap-4 w-full md:min-w-[60vh] transition-all duration-300'>
              <h1>Ver progreso</h1>
              <OrderReferralProgressBar ticketsSold={totals.totalTickets} />
              <p>Tu comprador lleva <span className='text-text-primary font-medium'>{promo.currentReward}%</span> de reintegro</p>
              <h1 className='border-b border-border-tertiary pb-1'>Ventas por link</h1>
              <div className="grid grid-cols-3 text-center">
                <div className="flex flex-col">
                  <span className="text-text-tertiary text-sm">Órdenes</span>
                  <span className="text-text-primary text-xl font-semibold">{totals.totalOrders}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-text-tertiary text-sm">Entradas</span>
                  <span className="text-text-primary text-xl font-semibold">{totals.totalTickets}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-text-tertiary text-sm">Monto</span>
                  <span className="text-text-primary text-xl font-semibold">{formatCurrency(totals.totalAmount)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default ReferralsTable;
